import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'

import { ResidentCell } from '@/components/business/units'
import { Badge } from '@/components/ui/badge'
import { TableColumn } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { routerFactory } from '@/factories'
import { UnitLeaseWithResidents } from '@/models/features'
import { formatDateLong } from '@/utils/date'
import { getLeaseHolder, getResidentFullName } from '@/utils/units'

import * as S from '../../units.page.styled'
import { getLeaseStatusColor, getLeaseStatusLabel } from '../../units.page.utils'

export const UNIT_LEASES_TABLE_ID = 'unit-leases'

interface GetUnitLeasesColumnsProps {
  t: TFunction<['units']>
}

export const getUnitLeasesColumns = ({
  t,
}: GetUnitLeasesColumnsProps): TableColumn<UnitLeaseWithResidents>[] => [
  {
    id: 'leaseId',
    render: (lease) => lease.pmsLeaseId,
    title: t('units:leases.columns.leaseId'),
    width: '16rem',
  },
  {
    id: 'leaseStatus',
    render: (lease) =>
      lease.leaseStatus ? (
        <Badge color={getLeaseStatusColor(lease.leaseStatus)} size="small" variant="light">
          {getLeaseStatusLabel(lease.leaseStatus, t)}
        </Badge>
      ) : (
        EMPTY_VALUE
      ),
    title: t('units:leases.columns.leaseStatus'),
    width: '17rem',
  },
  {
    id: 'startDate',
    render: (lease) => formatDateLong(lease.leaseStart),
    title: t('units:leases.columns.startDate'),
    width: '17rem',
  },
  {
    id: 'endDate',
    render: (lease) => formatDateLong(lease.leaseEnd),
    title: t('units:leases.columns.endDate'),
    width: '17rem',
  },
  {
    id: 'leaseHolder',
    render: (lease) => {
      const leaseHolder = getLeaseHolder(lease.residents)
      if (!leaseHolder) return EMPTY_VALUE

      const otherResidents = lease.residents.filter((resident) => resident !== leaseHolder)

      return (
        <S.ResidentsCell>
          <ResidentCell resident={leaseHolder} />
          {otherResidents.length > 0 && (
            <Tooltip
              placement="bottom"
              trigger={
                <Badge color="blue" size="small" variant="solid">
                  {`+${otherResidents.length}`}
                </Badge>
              }
            >
              <S.TooltipTitle>{t('units:leases.residentsTooltip.title')}</S.TooltipTitle>
              <S.TooltipList>
                {otherResidents.map((resident) => (
                  <li key={resident.clientId ?? resident.email}>
                    <Typography as="span" size="text-medium" weight="medium" color="white">
                      {resident.clientId === null ? (
                        getResidentFullName(resident) || resident.email
                      ) : (
                        <Link
                          to={routerFactory.getPathString({
                            params: { clientId: resident.clientId },
                            path: '/dashboard/clients/:clientId',
                          })}
                          style={{ color: 'inherit', textDecoration: 'underline' }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {getResidentFullName(resident) || resident.email}
                        </Link>
                      )}
                    </Typography>
                  </li>
                ))}
              </S.TooltipList>
            </Tooltip>
          )}
        </S.ResidentsCell>
      )
    },
    title: t('units:leases.columns.leaseHolder'),
    width: '34rem',
  },
]
